import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'


interface NutritionData {
  product: string
  serving: string
  energy: string
  fat: string
  saturated: string
  carbs: string
  sugars: string
  protein: string
  salt: string
}

export function NutritionalValuesSection() {
  const { t } = useTranslation('common')

  const products: NutritionData[] = [
    {
      product: t('nutrition.values.products.mortadella.name'),
      serving: '30g',
      energy: '98 kcal',
      fat: '8.5g',
      saturated: '3.2g',
      carbs: '0.5g',
      sugars: '0.3g',
      protein: '5.2g',
      salt: '0.8g',
    },
    {
      product: t('nutrition.values.products.prosciuttoCotto.name'),
      serving: '30g',
      energy: '42 kcal',
      fat: '1.8g',
      saturated: '0.6g',
      carbs: '0.2g',
      sugars: '0.1g',
      protein: '6.5g',
      salt: '0.7g',
    },
    {
      product: t('nutrition.values.products.guanciale.name'),
      serving: '30g',
      energy: '125 kcal',
      fat: '12.5g',
      saturated: '4.8g',
      carbs: '0g',
      sugars: '0g',
      protein: '3.2g',
      salt: '0.9g',
    },
    {
      product: t('nutrition.values.products.salame.name'),
      serving: '30g',
      energy: '115 kcal',
      fat: '9.5g',
      saturated: '3.8g',
      carbs: '0.3g',
      sugars: '0.1g',
      protein: '7.2g',
      salt: '1.1g',
    },
  ]

  const nutrients = [
    { key: 'energy', label: t('nutrition.values.table.energy'), unit: '' },
    { key: 'fat', label: t('nutrition.values.table.fat'), unit: '' },
    { key: 'saturated', label: t('nutrition.values.table.saturated'), unit: '' },
    { key: 'carbs', label: t('nutrition.values.table.carbs'), unit: '' },
    { key: 'sugars', label: t('nutrition.values.table.sugars'), unit: '' },
    { key: 'protein', label: t('nutrition.values.table.protein'), unit: '' },
    { key: 'salt', label: t('nutrition.values.table.salt'), unit: '' },
  ]

  return (
    <section className="py-16 md:py-24 bg-[#FDF8F3] relative">
      <div className="container-fiorucci">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 bg-[#DF631E]/10 text-[#DF631E] text-sm font-medium rounded-full mb-4">
            {t('nutrition.values.badge')}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#4E2118] mb-6">
            {t('nutrition.values.title')}
          </h2>
          <p className="text-lg text-[#4E2118]/70 leading-relaxed">
            {t('nutrition.values.subtitle')}
          </p>
        </div>

        {/* Nutrition Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12">
          {/* Table Header */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-[#4E2118] text-white">
                  <th className="px-6 py-4 text-left font-semibold text-sm uppercase tracking-wider">
                    {t('nutrition.values.table.product')}
                  </th>
                  <th className="px-4 py-4 text-center font-semibold text-sm uppercase tracking-wider">
                    {t('nutrition.values.table.serving')}
                  </th>
                  {nutrients.map((nutrient) => (
                    <th
                      key={nutrient.key}
                      className="px-4 py-4 text-center font-semibold text-sm uppercase tracking-wider"
                    >
                      {nutrient.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr
                    key={product.product}
                    className={`border-b border-[#4E2118]/5 last:border-b-0 hover:bg-[#FDF8F3]/50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-[#FDF8F3]/30'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <span className="font-medium text-[#4E2118]">{product.product}</span>
                    </td>
                    <td className="px-4 py-4 text-center text-[#4E2118]/70">
                      {product.serving}
                    </td>
                    <td className="px-4 py-4 text-center text-[#4E2118]/70">{product.energy}</td>
                    <td className="px-4 py-4 text-center text-[#4E2118]/70">{product.fat}</td>
                    <td className="px-4 py-4 text-center text-[#4E2118]/70">{product.saturated}</td>
                    <td className="px-4 py-4 text-center text-[#4E2118]/70">{product.carbs}</td>
                    <td className="px-4 py-4 text-center text-[#4E2118]/70">{product.sugars}</td>
                    <td className="px-4 py-4 text-center font-medium text-[#00441C]">{product.protein}</td>
                    <td className="px-4 py-4 text-center text-[#4E2118]/70">{product.salt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Note */}
        <div className="flex items-start gap-3 max-w-3xl mx-auto p-4 bg-[#DF631E]/5 rounded-xl border border-[#DF631E]/10">
          <Info className="w-5 h-5 text-[#DF631E] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4E2118]/70">
            {t('nutrition.values.disclaimer')}
          </p>
        </div>

        {/* Key Highlights */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {['quality', 'balance', 'protein', 'natural'].map((key) => (
            <div
              key={key}
              className="p-6 bg-white rounded-xl border border-[#4E2118]/5 shadow-sm hover:shadow-md transition-shadow"
            >
              <h3 className="font-serif text-lg font-semibold text-[#4E2118] mb-2">
                {t(`nutrition.values.highlights.${key}.title`)}
              </h3>
              <p className="text-sm text-[#4E2118]/70">
                {t(`nutrition.values.highlights.${key}.description`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
